import { DemoError } from "./demo.js";
import { supabase } from "./supabase.js";

const BUCKET = "avatars";

/**
 * The longest edge of a stored avatar. It is rendered at 28px on a board row and 56px on a
 * profile, so 256 covers a 3x display with room to spare. Resizing here rather than storing
 * the original is what keeps this inside a free storage tier and off a paid image service:
 * a phone photo is three or four megabytes, and this lands around thirty kilobytes.
 */
const MAX_EDGE = 256;

/**
 * How the image meets the square. `cover` centre-crops, so the square is full and the edges
 * are lost; `contain` scales the whole image to fit, so nothing is lost and the unused
 * space is left transparent.
 */
export type SquareFit = "cover" | "contain";

/**
 * Draw the image into a square canvas and re-encode it.
 *
 * Avatars centre-crop because every one is displayed in a circle, so the edges are cut off
 * anyway — cropping deliberately beats having it happen by accident. A logo is the opposite:
 * a crest or wordmark is destroyed by cropping, so it is contained.
 */
export async function toSquareWebp(file: File, fit: SquareFit = "cover"): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const edge =
    fit === "cover" ? Math.min(bitmap.width, bitmap.height) : Math.max(bitmap.width, bitmap.height);
  const size = Math.min(edge, MAX_EDGE);

  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;

  const context = canvas.getContext("2d");
  if (!context) throw new Error("Your browser could not process that image.");

  if (fit === "cover") {
    context.drawImage(
      bitmap,
      (bitmap.width - edge) / 2,
      (bitmap.height - edge) / 2,
      edge,
      edge,
      0,
      0,
      size,
      size,
    );
  } else {
    const scale = size / edge;
    const width = bitmap.width * scale;
    const height = bitmap.height * scale;
    context.drawImage(bitmap, (size - width) / 2, (size - height) / 2, width, height);
  }
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    // WebP at 0.85 is indistinguishable at this size and roughly half the bytes of JPEG.
    // Every browser that supports createImageBitmap encodes WebP.
    canvas.toBlob(resolve, "image/webp", 0.85),
  );

  if (!blob) throw new Error("Your browser could not process that image.");
  return blob;
}

/**
 * Upload a new avatar and point the member row at it.
 *
 * Returns the URL to store and display. It carries a cache-busting query because the
 * object path is stable — `<uid>/avatar.webp` is overwritten in place, so without it a
 * member would upload a new picture and keep seeing the old one until the CDN expired it.
 */
export async function uploadAvatar(userId: string, file: File): Promise<string> {
  if (!supabase) throw new DemoError();

  if (!file.type.startsWith("image/")) {
    throw new Error("That is not an image.");
  }

  const blob = await toSquareWebp(file);
  const path = `${userId}/avatar.webp`;

  const { error: uploadError } = await supabase.storage
    .from(BUCKET)
    .upload(path, blob, { contentType: "image/webp", upsert: true });

  if (uploadError) throw new Error(uploadError.message);

  const {
    data: { publicUrl },
  } = supabase.storage.from(BUCKET).getPublicUrl(path);

  const url = `${publicUrl}?v=${Date.now()}`;

  const { error } = await supabase.from("members").update({ avatar_url: url }).eq("id", userId);
  if (error) throw new Error(error.message);

  return url;
}
